import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import * as S from '@/components/Ad/Settings/Settings.styles';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import {
  EditPopupType,
  RemoveAdPayload,
} from '@/components/Ad/Settings/Settings.types';
import { editPopupData } from '@/components/Ad/Settings/Settings.constants';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { DESTROY_AD } from '@/api/mutations/ads.mutations';
import { AdStatus } from '@/types';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useAdDetails } from '@/hooks/useAdDetails';
import { DeleteAdModal } from '@/components/Ad/DeleteAdModal';
import { EDIT_AD_PAGE_SLUG } from '@/hooks/usePageType/usePageType.constants';
import { useServerDataContext } from '@/contexts';
import { GetAdHasEditPendingApprovalResponse } from '@/api/types/ads.types';
import { GET_AD_HAS_EDIT_PENDING_APPROVAL } from '@/api/queries/ads.queries';
import { getPathWithoutQueryParams } from '@/utils/helpers';
import { QUERY } from '@/constants/common';
import { WarningModal } from '@/components/Ad/WarningModal';
import { Promotions } from '@/components/Ad/Promotions/Promotions';
import { AdKind } from '@/api/types/common.types';
import { useResponsive } from '@/hooks/useResponsive';

const PromotionFlow = dynamic(
  () =>
    import('@/components/Ad/Promotions/PromotionFlow').then(
      mod => mod.PromotionFlow
    ),
  { ssr: false }
);

export const Settings = () => {
  const { t } = useTranslation();
  const { adDetails, refetch: refetchAdDetails } = useAdDetails();
  const { isDesktop } = useResponsive();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const { status, editLockedUntil, id, deleteReasons } = adData ?? {};
  const customPush = useCustomPush();
  const {
    query: { slug },
    replace,
  } = useRouter();
  const legacyId = Array.isArray(slug) ? slug[2] : undefined;
  const [isEditOpen, toggleEdit] = useToggleOpenState();
  const [isRemoveOpen, toggleRemove] = useToggleOpenState(false);
  const [isEditPendingOpen, toggleEditPending] = useToggleOpenState();
  const [isDestroying, setIsDestroying] = useState(false);
  const [isPromotionFlowOpen, togglePromotionFlow] = useToggleOpenState();
  const [selectedPromotionType, setSelectedPromotionType] =
    useState<AdKind | null>(null);
  const { url, hash } = useUrlAndHashFromPath() as {
    url: string;
    hash: EditPopupType;
  };
  const adPageBasePath = getPathWithoutQueryParams(url, [
    QUERY.transitionSourceBlock,
  ]);
  const canEdit = status === AdStatus.Published;
  const canRemove = status
    ? [AdStatus.Pending, AdStatus.Published, AdStatus.AwaitingPayment].includes(
        status
      )
    : false;

  function closeEditPopup() {
    toggleEdit(false);
    replace(adPageBasePath, undefined, { scroll: false });
  }

  const [destroyAd] = useMutation(DESTROY_AD, {
    onCompleted: () => {
      refetchAdDetails().then(() => setIsDestroying(false));
    },
  });

  const [
    checkHasEditPendingApproval,
    { loading: isEditPendingApprovalLoading },
  ] = useLazyQuery<GetAdHasEditPendingApprovalResponse>(
    GET_AD_HAS_EDIT_PENDING_APPROVAL,
    {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    }
  );

  async function edit() {
    if (isEditPendingApprovalLoading) return;

    const { data } = await checkHasEditPendingApproval({
      variables: { legacyId },
    });

    const hasEditPendingApproval = data?.ad?.hasEditPendingApproval ?? false;

    if (hasEditPendingApproval) {
      toggleEditPending(true);
      return;
    }

    if (editLockedUntil) {
      replace(`${adPageBasePath}/#${EditPopupType.Error}`, undefined, {
        scroll: false,
      });
      return;
    }

    customPush(`${adPageBasePath}/${EDIT_AD_PAGE_SLUG}`);
  }

  async function remove(data: RemoveAdPayload) {
    if (!canRemove || isDestroying) return;

    setIsDestroying(true);
    await destroyAd({
      variables: {
        adId: id,
        deleteReasonId: data.id,
        deleteReasonDescription: data?.delete_reason_description ?? '',
      },
    });
    toggleRemove(false);
  }

  function handleSelectPromotion(promotionType: AdKind) {
    setSelectedPromotionType(promotionType);
    togglePromotionFlow();
  }

  useEffect(() => {
    if (hash === EditPopupType.Error || hash === EditPopupType.OnReview) {
      toggleEdit(true);
    }
  }, [hash]);

  if (!adData) return null;

  const editDateTime = editLockedUntil
    ? format(new Date(editLockedUntil), 'dd.MM.yyyy HH:mm')
    : undefined;
  const editPopupTexts = editPopupData(t, editDateTime)[hash];
  const { title: editPopupTitle, description: editPopupDescription } =
    editPopupTexts ?? {};

  if (status === AdStatus.Rejected) {
    return null;
  }

  return (
    <>
      <S.Box>
        {(canEdit || canRemove) && (
          <S.Actions>
            {canEdit && (
              <S.SettingsButton
                appearance="secondaryOutlined"
                isFullWidth
                onClick={edit}
                data-testid={AdPageTestIds.settings.editButton}
                disabled={isEditPendingApprovalLoading}
              >
                {t('common.edit')}
              </S.SettingsButton>
            )}
            {canRemove && (
              <S.SettingsButton
                appearance="redOutlined"
                isFullWidth
                onClick={toggleRemove}
                data-testid={AdPageTestIds.settings.remove.button}
              >
                {t('pages.ad.remove_ad')}
              </S.SettingsButton>
            )}

            {canRemove && (
              <DeleteAdModal
                submit={remove}
                toggle={toggleRemove}
                isOpen={isRemoveOpen}
                reasons={deleteReasons ?? []}
              />
            )}
          </S.Actions>
        )}

        <Promotions
          appearance={isDesktop ? 'primary' : 'secondary'}
          onSelectPromotion={handleSelectPromotion}
        />
      </S.Box>

      {editPopupTexts && (
        <WarningModal
          dataTestId={AdPageTestIds.settings.editPopup}
          isOpen={isEditOpen}
          onClose={closeEditPopup}
          title={editPopupTitle ?? ''}
          description={editPopupDescription ?? ''}
          {...(hash === EditPopupType.OnReview && {
            icon: 'check-circle-rounded.svg',
          })}
        />
      )}

      <WarningModal
        dataTestId={AdPageTestIds.settings.editPendingPopup}
        isOpen={isEditPendingOpen}
        onClose={toggleEditPending}
        title={t('pages.ad.edit_pending_approval')}
        description={t('pages.ad.edit_pending_approval_description')}
      />

      <PromotionFlow
        isInPortal
        isOpen={isPromotionFlowOpen}
        toggle={togglePromotionFlow}
        type={selectedPromotionType}
      />
    </>
  );
};
